import { Component, Input, OnChanges, SimpleChanges, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FilterOption } from '../../services/filter-meta.service';

/**
 * Provider Expression that allows azc-chip-option-list to register as a ControlValueAccessor.
 * This allows it to support [(ngModel)].
 * @docs-private
 */
export const AZC_CHIP_OPTION_LIST_CONTROL_VALUE_ACCESSOR: any = {
	provide: NG_VALUE_ACCESSOR,
	// tslint:disable-next-line
	useExisting: forwardRef(() => ChipOptionListComponent),
	multi: true
};
@Component({
	selector: 'azc-chip-option-list',
	templateUrl: './chip-option-list.component.html',
	styleUrls: ['./chip-option-list.component.scss'],
	providers: [AZC_CHIP_OPTION_LIST_CONTROL_VALUE_ACCESSOR]
})
export class ChipOptionListComponent implements OnChanges, ControlValueAccessor {
	@Input() public options: FilterOption[];
	@Input() public limitTo = 5;
	@Input() public limitMax = 1000;
	@Input() public extraClass: string;
	@Input() public isVertical: boolean;
	@Input() public orderByName: boolean;
	private onChange: (value) => void;
	public isDisabled: boolean;
	private value: any[];
	public onTouched: () => {} = () => null;
	public get filteredOptions(): FilterOption[] {
		let os = this.options;
		if (this.orderByName) {
			os = os && os.sort(function (a, b) { return a.name > b.name ? 1 : -1; });
		} else {
		}
		return os && os.slice(0, this.limitTo);
	}

	constructor() { }

	ngOnChanges(changes: SimpleChanges): void {
		if (!changes.options) return;

		this.sortOptions();
		if (!changes.options.firstChange) {
			const newValue = this.options ? this.options.filter(x => x.selected).map(x => x.value) : [];

			if (newValue.length !== this.value.length || !newValue.every((v, i) => v === this.value[i])) {
				this.onChange(newValue);
			}
		}
	}

	private sortOptions() {
		if (!this.options) return;

		const value = this.value || [];
		this.options.forEach(x => x.selected = value.includes(x.value));

		if (this.limitTo < this.options.length) {
			this.options = this.options.sort((x, y) => {
				return x.selected && !y.selected ? -1 : y.selected && !x.selected ? 1 : x.value - y.value;
			});
		}
	}

	writeValue(value: any[] | any): void {
		value = value || [];

		if (!Array.isArray(value)) {
			value = [value];
		}
		this.value = value;
		this.sortOptions();
	}

	public onSelect(option: FilterOption, isSelected: boolean) {
		this.onTouched();
		if (!option.selected === !isSelected) return;
		option.selected = isSelected;

		const values = this.options.filter(x => x.selected).map(x => x.value);

		this.onChange(values);
	}

	registerOnChange(fn: (value: any) => void) {
		this.onChange = fn;
	}

	registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}

	setDisabledState?(isDisabled: boolean): void {
		this.isDisabled = isDisabled;
	}

	public expandOptions() {
		this.limitTo = this.options.length;
	}

	public getClass(option: FilterOption): string[] {
		const classes = [option.name.replace(/ /g, '').toLowerCase(), this.extraClass || 'default'];

		if (option.selected) classes.push('selected');

		return classes;
	}

	public showMore() {
		return this.options && this.options.length > this.limitTo && this.options.length < this.limitMax;
	}
}
